import { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Skeleton,
  TextField,
  Autocomplete,
  Chip,
  InputAdornment,
  LinearProgress,
  Tooltip as MuiTooltip,
  FormControlLabel,
  Switch,
  Button,
  Snackbar,
  Select,
  MenuItem,
  IconButton,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ChatIcon from '@mui/icons-material/Chat';
import EmailIcon from '@mui/icons-material/Email';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { format, subDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useOnboardingStore } from '../stores/onboardingStore';
import { useFilterStore } from '../stores/filterStore';
import { useExclusionsStore } from '../stores/exclusionsStore';
import { formatOrgDisplayName } from '../utils/display';
import {
  getOnboardingUsers,
  getHoursSavedSummary,
  updateCSOnboardingStatus,
  updateCSTags,
  listCSTags,
  fetchUserCSContextRaw,
  type OnboardingFilters,
} from '../api/dashboard';
import UserRequestsModal from '../components/UserRequestsModal';
import type {
  CSOnboardingStatus,
  FilterOption,
  HoursSavedSummary,
  OnboardingStatus,
  StateTag,
  UserOnboarding,
} from '../types';

const STATUS_COLORS: Record<OnboardingStatus, string> = {
  pre: '#64748b',
  in_progress: '#f59e0b',
  onboarded: '#10b981',
};

const STATUS_LABELS: Record<OnboardingStatus, string> = {
  pre: 'Pre',
  in_progress: 'In Progress',
  onboarded: 'Onboarded',
};

const CS_STATUS_LABELS: Record<CSOnboardingStatus, string> = {
  not_scheduled: 'Not Scheduled',
  first_session_scheduled: '1st Session Scheduled',
  second_session_scheduled: '2nd Session Scheduled',
  completed: 'Completed',
};

const CS_STATUS_COLORS: Record<CSOnboardingStatus, string> = {
  not_scheduled: '#64748b',           // slate — not engaged
  first_session_scheduled: '#f59e0b', // amber — early progress
  second_session_scheduled: '#3b82f6', // blue — further along
  completed: '#10b981',                // green — done
};

const CS_STATUS_OPTIONS: CSOnboardingStatus[] = [
  'not_scheduled',
  'first_session_scheduled',
  'second_session_scheduled',
  'completed',
];

const STATE_TAG_LABELS: Record<StateTag, string> = {
  stalled_pre: 'Stalled (Pre)',
  stalled_mid: 'Stalled (Mid)',
  velocity_drop: 'Velocity Drop',
  dormant: 'Dormant',
  power_user: 'Power User',
  healthy_active: 'Healthy',
};

// Tag colors mirror the dashboard palette. Negative-signal tags use the same
// red family as the legacy at-risk chip; `power_user` is celebratory; `healthy_active`
// is a muted slate so a healthy row stays visually quiet.
const STATE_TAG_COLORS: Record<StateTag, { bg: string; fg: string }> = {
  stalled_pre: { bg: 'rgba(245, 158, 11, 0.20)', fg: '#f59e0b' },     // amber
  stalled_mid: { bg: 'rgba(249, 115, 22, 0.20)', fg: '#f97316' },     // orange
  velocity_drop: { bg: 'rgba(239, 68, 68, 0.20)', fg: '#ef4444' },    // red
  dormant: { bg: 'rgba(220, 38, 38, 0.30)', fg: '#dc2626' },          // deeper red
  power_user: { bg: 'rgba(139, 92, 246, 0.25)', fg: '#a78bfa' },      // violet (brand accent)
  healthy_active: { bg: 'rgba(100, 116, 139, 0.20)', fg: '#94a3b8' }, // muted slate
};

const STATE_TAG_FILTER_OPTIONS: StateTag[] = [
  'stalled_pre',
  'stalled_mid',
  'velocity_drop',
  'dormant',
  'power_user',
  'healthy_active',
];

function describeStateTag(tag: StateTag, user: UserOnboarding): string {
  // Tooltip text — the rule that fired, expressed in numbers from this user's row.
  switch (tag) {
    case 'stalled_pre':
      return `Signed up but no task activity yet (status: ${user.onboardingStatus}).`;
    case 'stalled_mid':
      return `Onboarding in progress, missing pillar — workflows: ${user.workflowCount}, projects: ${user.projectCount}.`;
    case 'velocity_drop':
      return `Activity dropped: ${user.events7d} events last 7d vs ${user.eventsPrior7d} prior 7d.`;
    case 'dormant':
      return 'No login and no task activity in the last 30 days.';
    case 'power_user':
      return `High activity (${user.events30d} events last 30d), at or above org average.`;
    case 'healthy_active':
      return 'No risk or stalled signals firing.';
  }
}

const CSV_COLUMNS: Array<{ header: string; get: (u: UserOnboarding) => string | number | boolean }> = [
  { header: 'user_id', get: (u) => u.userId },
  { header: 'first_name', get: (u) => u.firstName },
  { header: 'last_name', get: (u) => u.lastName },
  { header: 'username', get: (u) => u.username },
  { header: 'email', get: (u) => u.email || '' },
  { header: 'org_id', get: (u) => u.orgId || '' },
  { header: 'org_name', get: (u) => u.orgName || '' },
  { header: 'account_type', get: (u) => u.accountType },
  { header: 'is_active', get: (u) => u.isActive },
  { header: 'created_at', get: (u) => u.createdAt || '' },
  { header: 'last_login', get: (u) => u.lastLogin || '' },
  { header: 'last_activity', get: (u) => u.lastActivity || '' },
  { header: 'onboarding_status', get: (u) => u.onboardingStatus },
  { header: 'cs_onboarding_status', get: (u) => u.csOnboardingStatus },
  { header: 'cs_tags', get: (u) => u.csTags.join(',') },
  { header: 'first_value_moment_completed', get: (u) => u.firstValueMomentCompleted },
  { header: 'at_risk', get: (u) => u.atRisk },
  { header: 'state_tags', get: (u) => u.stateTags.join('|') },
  { header: 'events_7d', get: (u) => u.events7d },
  { header: 'events_prior_7d', get: (u) => u.eventsPrior7d },
  { header: 'events_30d', get: (u) => u.events30d },
  { header: 'query_count', get: (u) => u.queryCount },
  { header: 'workflow_count', get: (u) => u.workflowCount },
  { header: 'checklist_count', get: (u) => u.checklistCount },
  { header: 'project_count', get: (u) => u.projectCount },
];

function csvEscape(value: string | number | boolean): string {
  const str = String(value ?? '');
  // RFC 4180: wrap in quotes if value contains delimiter, quote, or newline; double inner quotes.
  return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

function buildOnboardingCsv(rows: UserOnboarding[]): string {
  // RFC-4180: CRLF record separator + trailing terminator. Matters for stricter
  // parsers (Excel on macOS, Python csv.reader with the excel dialect) when a
  // quoted field happens to contain a CRLF of its own.
  const lines = [CSV_COLUMNS.map((c) => c.header).join(',')];
  for (const row of rows) {
    lines.push(CSV_COLUMNS.map((c) => csvEscape(c.get(row))).join(','));
  }
  return lines.join('\r\n') + '\r\n';
}

function CSStatusDot({ status, size = 8 }: { status: CSOnboardingStatus; size?: number }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: CS_STATUS_COLORS[status],
        mr: 1,
        flexShrink: 0,
      }}
    />
  );
}

interface CSTagsCellProps {
  tags: string[];
  knownTags: string[];
  onChange: (tags: string[]) => void;
}

function CSTagsCell({ tags, knownTags, onChange }: CSTagsCellProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState('');
  const open = Boolean(anchorEl);

  const closePopover = () => {
    setAnchorEl(null);
    setQuery('');
  };

  // Cell only emits raw additions/removals. Parent's canonicalizeCsTags is the
  // single source of truth for trim/dedup/sort.
  const addTag = (tag: string) => {
    if (!tag.trim()) return;
    onChange([...tags, tag]);
    closePopover();
  };

  const removeTag = (tag: string) => {
    onChange(tags.filter((t) => t !== tag));
  };

  const search = query.trim();
  const searchLower = search.toLowerCase();
  const onUser = (t: string) => tags.some((existing) => existing.toLowerCase() === t.toLowerCase());
  const suggestions = knownTags
    .filter((t) => !onUser(t))
    .filter((t) => !searchLower || t.toLowerCase().includes(searchLower));
  const exactMatch = knownTags.some((t) => t.toLowerCase() === searchLower) || onUser(search);
  const showCreate = !!search && !exactMatch;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
      {tags.map((tag) => (
        <Chip
          key={tag}
          label={tag}
          size="small"
          onDelete={() => removeTag(tag)}
          sx={{ height: 20, fontSize: '0.7rem' }}
        />
      ))}
      <IconButton
        size="small"
        aria-label="Add CS tag"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ p: 0.25, border: '1px dashed', borderColor: 'divider', borderRadius: '50%' }}
      >
        <AddIcon sx={{ fontSize: 14 }} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1, minWidth: 220 }}>
          <TextField
            autoFocus
            size="small"
            fullWidth
            placeholder="Search or create tag..."
            inputProps={{ 'aria-label': 'Search or create CS tag' }}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (suggestions.length > 0) {
                  addTag(suggestions[0]);
                } else if (showCreate) {
                  addTag(search);
                }
              } else if (e.key === 'Escape') {
                closePopover();
              }
            }}
          />
          <List dense sx={{ mt: 0.5, maxHeight: 200, overflow: 'auto' }}>
            {suggestions.map((tag) => (
              <ListItemButton key={tag} onClick={() => addTag(tag)} dense>
                <ListItemText primary={tag} primaryTypographyProps={{ fontSize: '0.8rem' }} />
              </ListItemButton>
            ))}
            {showCreate && (
              <ListItemButton onClick={() => addTag(search)} dense>
                <ListItemText
                  primary={`Create "${search}"`}
                  primaryTypographyProps={{ fontSize: '0.8rem', color: 'primary.main', fontStyle: 'italic' }}
                />
              </ListItemButton>
            )}
            {suggestions.length === 0 && !showCreate && (
              <ListItemText
                primary="No tags yet — type to create"
                primaryTypographyProps={{ fontSize: '0.75rem', color: 'text.secondary', px: 2, py: 1 }}
              />
            )}
          </List>
        </Box>
      </Popover>
    </Box>
  );
}

export default function OnboardingDashboard() {
  const {
    users,
    isLoading,
    selectedOrgIds,
    availableOrgs,
    statusFilter,
    searchQuery,
    sortModel,
    setUsers,
    setLoading,
    setSelectedOrgIds,
    setAvailableOrgs,
    setStatusFilter,
    setSearchQuery,
    setSortModel,
  } = useOnboardingStore();
  const { availableOptions, isOptionsLoaded } = useFilterStore();
  const { excludedOrgIds, effectiveExcludedUserIds } = useExclusionsStore();

  const navigate = useNavigate();

  // State for hours saved summary (last 30 days)
  const [hoursSavedSummary, setHoursSavedSummary] = useState<HoursSavedSummary | null>(null);
  const [isHoursSavedLoading, setIsHoursSavedLoading] = useState(false);

  // State for hiding lite users
  const [hideLiteUsers, setHideLiteUsers] = useState(false);

  // State for showing inactive users (pilot users marked as active=false)
  const [showInactive, setShowInactive] = useState(false);

  // CS-platform filters (PEL-1223)
  const [csStatusFilter, setCsStatusFilter] = useState<CSOnboardingStatus | ''>('');
  const [csTagFilter, setCsTagFilter] = useState<string[]>([]);
  const [fvmFilter, setFvmFilter] = useState<'' | 'true' | 'false'>('');
  const [atRiskFilter, setAtRiskFilter] = useState<'' | 'true' | 'false'>('');
  const [stateTagFilter, setStateTagFilter] = useState<StateTag | ''>('');
  // Tracks the user_id whose CS context is currently being pre-fetched for the
  // FU button. Doubles as a guard against double-clicks racing each other into
  // conflicting navigations.
  const [loadingFollowUpUserId, setLoadingFollowUpUserId] = useState<string | null>(null);

  // Tag suggestions for the per-row chip input + the filter dropdown.
  const [knownTags, setKnownTags] = useState<string[]>([]);

  // Snackbar for soft notifications (FU coming-soon, mutation errors).
  const [snack, setSnack] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });
  const showSnack = useCallback((message: string) => setSnack({ open: true, message }), []);

  useEffect(() => {
    listCSTags()
      .then(setKnownTags)
      .catch((err) => console.error('Failed to load CS tags:', err));
  }, []);

  // State for user requests modal
  const [requestsModal, setRequestsModal] = useState<{
    open: boolean;
    userId: string;
    userName: string;
    requestType: 'query' | 'workflow';
    requestCount: number;
  }>({
    open: false,
    userId: '',
    userName: '',
    requestType: 'query',
    requestCount: 0,
  });

  const openRequestsModal = useCallback(
    (user: UserOnboarding, type: 'query' | 'workflow') => {
      const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || 'User';
      setRequestsModal({
        open: true,
        userId: user.userId,
        userName: fullName,
        requestType: type,
        requestCount: type === 'query' ? user.queryCount : user.workflowCount,
      });
    },
    []
  );

  const closeRequestsModal = useCallback(() => {
    setRequestsModal((prev) => ({ ...prev, open: false }));
  }, []);

  // Track initialization to prevent cascading refetches
  const isInitializedRef = useRef(false);
  const isFetchingRef = useRef(false);

  // Backend handles only org-scope filtering. Column filters (status, CS status,
  // CS tags, FVM, At Risk, search) are applied client-side from the fetched list.
  const buildOrgFilters = useCallback((): OnboardingFilters => ({
    orgIds: selectedOrgIds.length > 0 ? selectedOrgIds : undefined,
    excludeOrgIds: excludedOrgIds.length > 0 ? excludedOrgIds : undefined,
    excludeUserIds: effectiveExcludedUserIds.length > 0 ? effectiveExcludedUserIds : undefined,
    includeInactive: showInactive,
  }), [selectedOrgIds, excludedOrgIds, effectiveExcludedUserIds, showInactive]);

  const fetchData = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    setLoading(true);
    try {
      const sortField = sortModel[0]?.field || 'createdAt';
      const sortOrder = sortModel[0]?.sort || 'desc';

      const fieldMapping: Record<string, string> = {
        createdAt: 'created_at',
        lastActivity: 'last_activity',
        lastLogin: 'last_login',
        userName: 'user_name',
        email: 'email',
        totalEvents: 'total_events',
        queryCount: 'query_count',
        workflowCount: 'workflow_count',
        checklistCount: 'checklist_count',
      };
      const backendSortField = fieldMapping[sortField] || sortField;
      const filters = buildOrgFilters();

      const usersData = await getOnboardingUsers(filters, backendSortField, sortOrder);
      setUsers(usersData.users);

      // Hours saved is a separate, heavy calculation (last 30 days).
      const endDate = new Date();
      const startDate = subDays(endDate, 30);
      setIsHoursSavedLoading(true);
      getHoursSavedSummary(
        startDate,
        endDate,
        filters.orgIds,
        filters.excludeOrgIds,
        filters.excludeUserIds,
      )
        .then(setHoursSavedSummary)
        .catch((err) => console.error('Failed to fetch hours saved:', err))
        .finally(() => setIsHoursSavedLoading(false));
    } catch (error) {
      console.error('Failed to fetch onboarding data:', error);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [buildOrgFilters, sortModel, setUsers, setLoading]);

  // Initial load: use orgs from filterStore (already loaded by AuthGuard) then fetch data
  useEffect(() => {
    if (isInitializedRef.current) return;

    const initialize = async () => {
      // Use orgs from filterStore if available (loaded by AuthGuard)
      if (isOptionsLoaded && availableOrgs.length === 0) {
        setAvailableOrgs(availableOptions.orgIds);
      }
      await fetchData();
      isInitializedRef.current = true;
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when org-scope inputs change (after initial load). Column filters
  // don't trigger a refetch — they re-derive `filteredUsers` in-memory.
  useEffect(() => {
    if (!isInitializedRef.current) return;
    fetchData();
  }, [fetchData]);

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
  };

  const handleStatusFilterClick = (status: OnboardingStatus | null) => {
    setStatusFilter(statusFilter === status ? null : status);
  };

  // Functional updates against the live store so concurrent edits to other rows
  // aren't clobbered by a stale snapshot when an in-flight request fails.
  const patchUserInStore = useCallback(
    (userId: string, patch: Partial<UserOnboarding>) => {
      useOnboardingStore.setState((state) => ({
        users: state.users.map((u) => (u.userId === userId ? { ...u, ...patch } : u)),
      }));
    },
    []
  );

  const handleCsStatusChange = useCallback(
    async (user: UserOnboarding, status: CSOnboardingStatus) => {
      const previous = user.csOnboardingStatus;
      patchUserInStore(user.userId, { csOnboardingStatus: status });
      try {
        await updateCSOnboardingStatus(user.userId, status);
      } catch (err) {
        console.error('Failed to update CS status:', err);
        showSnack('Failed to update onboarding status');
        patchUserInStore(user.userId, { csOnboardingStatus: previous });
      }
    },
    [patchUserInStore, showSnack]
  );

  // Single source of truth for CS-tag canonicalization: case-insensitive de-dup,
  // strip whitespace, drop empties, sort. Cell + filters call this directly.
  const canonicalizeCsTags = useCallback((raw: string[]): string[] => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const tag of raw) {
      const trimmed = tag.trim();
      if (!trimmed) continue;
      const key = trimmed.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(trimmed);
    }
    return out.sort((a, b) => a.localeCompare(b));
  }, []);

  const handleCsTagsChange = useCallback(
    async (user: UserOnboarding, tags: string[]) => {
      const cleaned = canonicalizeCsTags(tags);
      const previous = user.csTags;
      patchUserInStore(user.userId, { csTags: cleaned });
      try {
        await updateCSTags(user.userId, cleaned);
        setKnownTags((current) => canonicalizeCsTags([...current, ...cleaned]));
      } catch (err) {
        console.error('Failed to update CS tags:', err);
        showSnack('Failed to update tags');
        patchUserInStore(user.userId, { csTags: previous });
      }
    },
    [canonicalizeCsTags, patchUserInStore, showSnack]
  );

  const handleOpenUsageChat = useCallback(
    (user: UserOnboarding) => {
      const params = new URLSearchParams({ user_id: user.userId });
      const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
      if (fullName) params.set('user_name', fullName);
      if (user.email) params.set('user_email', user.email);
      navigate(`/chat?${params.toString()}`);
    },
    [navigate]
  );

  /**
   * Pre-fetch the per-user CS context, then open chat with a seeded thread.
   * Skips the agent's `get_user_cs_context` tool round-trip on the hot path —
   * the agent's first reply is already narration when the user lands on chat.
   *
   * `loadingFollowUpUserId` drives the row-level spinner and also blocks
   * concurrent clicks: if two rows fire in parallel, whichever finishes first
   * would navigate, discarding the other's result. The early return here
   * matches the per-row `disabled` on the IconButton — defense in depth.
   */
  const handleSendFollowUp = useCallback(
    async (user: UserOnboarding) => {
      if (loadingFollowUpUserId) return;
      setLoadingFollowUpUserId(user.userId);

      const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
      const greetingName = user.firstName || fullName || user.email || 'this user';
      const emailSuffix = user.email ? ` (${user.email})` : '';
      const seedMessage = `Help me draft an onboarding follow-up email for ${greetingName}${emailSuffix}.`;

      try {
        const csContextSeed = await fetchUserCSContextRaw(user.userId);
        navigate('/chat', {
          state: {
            csFollowUpSeed: {
              userId: user.userId,
              seedMessage,
              csContextSeed,
            },
          },
        });
        // Don't clear loading state on success — navigation unmounts this
        // page, so a leftover spinner is invisible. Clearing it here would
        // race with the unmount and cause a flicker on slow navigations.
      } catch (err) {
        console.error('Failed to fetch CS context for FU email', err);
        showSnack('Could not load this user\'s context — try again.');
        setLoadingFollowUpUserId(null);
      }
    },
    [loadingFollowUpUserId, navigate, showSnack]
  );

  // Apply every column-level filter client-side. Order is cheapest-first to
  // short-circuit early; ordering is functionally insignificant.
  const filteredUsers = useMemo(() => {
    const searchLower = searchQuery.trim().toLowerCase();
    const tagFilterLower = csTagFilter.map((t) => t.toLowerCase());
    return users.filter((user) => {
      if (hideLiteUsers && user.accountType === 'lite') return false;
      if (statusFilter && user.onboardingStatus !== statusFilter) return false;
      if (csStatusFilter && user.csOnboardingStatus !== csStatusFilter) return false;
      if (fvmFilter && (fvmFilter === 'true') !== user.firstValueMomentCompleted) return false;
      if (atRiskFilter && (atRiskFilter === 'true') !== user.atRisk) return false;
      if (stateTagFilter && !user.stateTags.includes(stateTagFilter)) return false;
      if (tagFilterLower.length > 0) {
        const userTagsLower = user.csTags.map((t) => t.toLowerCase());
        const hasAny = tagFilterLower.some((t) => userTagsLower.includes(t));
        if (!hasAny) return false;
      }
      if (searchLower) {
        const haystacks = [user.firstName, user.lastName, user.username, user.email]
          .map((s) => (s || '').toLowerCase());
        if (!haystacks.some((h) => h.includes(searchLower))) return false;
      }
      return true;
    });
  }, [users, hideLiteUsers, statusFilter, csStatusFilter, fvmFilter, atRiskFilter, stateTagFilter, csTagFilter, searchQuery]);

  // Derive the summary cards from the FE-filtered list so every filter affects
  // the totals and breakdown without a backend round-trip.
  const derivedSummary = useMemo(() => {
    const total = filteredUsers.length;
    let pre = 0;
    let inProgress = 0;
    let onboarded = 0;
    for (const user of filteredUsers) {
      if (user.onboardingStatus === 'pre') pre += 1;
      else if (user.onboardingStatus === 'in_progress') inProgress += 1;
      else if (user.onboardingStatus === 'onboarded') onboarded += 1;
    }
    const pct = (n: number) => (total > 0 ? Math.round((n / total) * 100) : 0);
    return {
      total,
      pre,
      inProgress,
      onboarded,
      prePercentage: pct(pre),
      inProgressPercentage: pct(inProgress),
      onboardedPercentage: pct(onboarded),
    };
  }, [filteredUsers]);

  const handleExportCsv = useCallback(() => {
    if (filteredUsers.length === 0) {
      showSnack('Nothing to export');
      return;
    }
    try {
      const csv = buildOnboardingCsv(filteredUsers);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      try {
        const link = document.createElement('a');
        link.href = url;
        link.download = `users_onboarding_${format(new Date(), 'yyyy-MM-dd')}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('CSV export failed:', err);
      showSnack('CSV export failed');
    }
  }, [filteredUsers, showSnack]);

  const columns: GridColDef<UserOnboarding>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 150,
        renderCell: (params) => {
          const fullName = [params.row.firstName, params.row.lastName]
            .filter(Boolean)
            .join(' ');
          const isInactive = params.row.isActive === false;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ color: isInactive ? 'text.disabled' : 'inherit' }}
              >
                {fullName || '-'}
              </Typography>
              {isInactive && (
                <Chip
                  label="Inactive"
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    bgcolor: 'rgba(239, 68, 68, 0.25)',
                    color: '#ef4444',
                    fontWeight: 500,
                  }}
                />
              )}
            </Box>
          );
        },
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 1.2,
        minWidth: 200,
        renderCell: (params) => {
          const email = params.row.email;
          if (!email) {
            return <Typography variant="body2" color="text.secondary">-</Typography>;
          }
          const copy = async () => {
            if (!navigator.clipboard) {
              showSnack('Clipboard unavailable in this browser');
              return;
            }
            try {
              await navigator.clipboard.writeText(email);
              showSnack(`Copied ${email}`);
            } catch {
              showSnack('Copy failed');
            }
          };
          return (
            <MuiTooltip title="Click to copy" arrow placement="top">
              <Typography
                variant="body2"
                color="text.secondary"
                role="button"
                tabIndex={0}
                aria-label={`Copy email ${email}`}
                onClick={(e) => {
                  e.stopPropagation();
                  copy();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    copy();
                  }
                }}
                sx={{
                  cursor: 'pointer',
                  outline: 'none',
                  '&:hover, &:focus-visible': { color: 'primary.main', textDecoration: 'underline' },
                }}
              >
                {email}
              </Typography>
            </MuiTooltip>
          );
        },
      },
      {
        field: 'orgName',
        headerName: 'Organization',
        flex: 1,
        minWidth: 150,
        renderCell: (params) => (
          <Typography variant="body2">
            {params.row.orgName ? formatOrgDisplayName(params.row.orgName) : '-'}
          </Typography>
        ),
      },
      {
        field: 'accountType',
        headerName: 'Type',
        width: 90,
        renderCell: (params) => {
          const type = params.row.accountType || 'pro';
          const colors: Record<string, string> = {
            lite: '#64748b',
            pro: '#3b82f6',
            enterprise: '#8b5cf6',
          };
          return (
            <Chip
              label={type}
              size="small"
              sx={{
                bgcolor: colors[type] || colors.pro,
                color: 'white',
                fontWeight: 500,
                fontSize: '0.7rem',
                height: 22,
              }}
            />
          );
        },
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        width: 110,
        renderCell: (params) =>
          params.value ? format(new Date(params.value), 'MMM d, yyyy') : '-',
      },
      {
        field: 'lastActivity',
        headerName: 'Last Activity',
        width: 110,
        renderCell: (params) =>
          params.value ? format(new Date(params.value), 'MMM d, yyyy') : '-',
      },
      {
        field: 'onboardingStatus',
        headerName: 'Status',
        width: 120,
        renderCell: (params) => {
          const status = params.value as OnboardingStatus;
          return (
            <Chip
              label={STATUS_LABELS[status]}
              size="small"
              sx={{
                bgcolor: STATUS_COLORS[status],
                color: 'white',
                fontWeight: 500,
              }}
            />
          );
        },
      },
      {
        field: 'csOnboardingStatus',
        headerName: 'CS Status',
        width: 200,
        sortable: false,
        renderCell: (params) => (
          <Select
            size="small"
            value={params.row.csOnboardingStatus}
            onChange={(e) => handleCsStatusChange(params.row, e.target.value as CSOnboardingStatus)}
            onClick={(e) => e.stopPropagation()}
            renderValue={(value) => (
              <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.75rem' }}>
                <CSStatusDot status={value as CSOnboardingStatus} />
                {CS_STATUS_LABELS[value as CSOnboardingStatus]}
              </Box>
            )}
            sx={{ fontSize: '0.75rem', minWidth: 180, height: 28 }}
          >
            {CS_STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt} sx={{ fontSize: '0.8rem' }}>
                <CSStatusDot status={opt} />
                {CS_STATUS_LABELS[opt]}
              </MenuItem>
            ))}
          </Select>
        ),
      },
      {
        field: 'csTags',
        headerName: 'CS Tags',
        width: 130,
        sortable: false,
        renderCell: (params) => (
          <Box onClick={(e) => e.stopPropagation()} sx={{ width: '100%' }}>
            <CSTagsCell
              tags={params.row.csTags || []}
              knownTags={knownTags}
              onChange={(next) => handleCsTagsChange(params.row, next)}
            />
          </Box>
        ),
      },
      {
        field: 'firstValueMomentCompleted',
        headerName: 'FVM',
        width: 70,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <MuiTooltip
            title="First Value Moment: created at least one project, ran at least one checklist, and ran at least one workflow."
            arrow
          >
            <Chip
              label={params.value ? 'Yes' : 'No'}
              size="small"
              sx={{
                bgcolor: params.value ? '#10b981' : 'rgba(100, 116, 139, 0.25)',
                color: params.value ? 'white' : 'text.secondary',
                fontWeight: 500,
                fontSize: '0.65rem',
                height: 20,
              }}
            />
          </MuiTooltip>
        ),
      },
      {
        field: 'stateTags',
        headerName: 'State',
        width: 220,
        // Sort by `at_risk` so the "needs attention" rows still cluster — chip
        // stacks themselves don't compare meaningfully via the default sorter.
        sortComparator: (_a, _b, p1, p2) => {
          const r1 = (p1.api.getRow(p1.id) as UserOnboarding | undefined)?.atRisk ? 1 : 0;
          const r2 = (p2.api.getRow(p2.id) as UserOnboarding | undefined)?.atRisk ? 1 : 0;
          return r1 - r2;
        },
        renderCell: (params) => {
          const tags = (params.value as StateTag[] | undefined) ?? [];
          const user = params.row as UserOnboarding;
          if (tags.length === 0) return null;
          return (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
              {tags.map((tag) => {
                const colors = STATE_TAG_COLORS[tag];
                return (
                  <MuiTooltip key={tag} title={describeStateTag(tag, user)} arrow>
                    <Chip
                      label={STATE_TAG_LABELS[tag]}
                      size="small"
                      sx={{
                        bgcolor: colors.bg,
                        color: colors.fg,
                        fontWeight: 500,
                        fontSize: '0.65rem',
                        height: 20,
                      }}
                    />
                  </MuiTooltip>
                );
              })}
            </Box>
          );
        },
      },
      {
        field: 'lastLogin',
        headerName: 'Last Login',
        width: 110,
        renderCell: (params) =>
          params.value ? format(new Date(params.value as string), 'MMM d, yyyy') : '—',
      },
      {
        field: 'rowActions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <MuiTooltip title="Open Usage Chat scoped to this user" arrow>
              <IconButton
                size="small"
                aria-label="Open usage chat"
                onClick={() => handleOpenUsageChat(params.row)}
              >
                <ChatIcon fontSize="small" />
              </IconButton>
            </MuiTooltip>
            <MuiTooltip
              title={
                loadingFollowUpUserId === params.row.userId
                  ? 'Loading user context…'
                  : 'Draft an onboarding FU email with the CS agent'
              }
              arrow
            >
              {/* span wrapper so the tooltip still fires while the IconButton is disabled */}
              <span>
                <IconButton
                  size="small"
                  aria-label="Draft onboarding follow-up email"
                  onClick={() => handleSendFollowUp(params.row)}
                  disabled={loadingFollowUpUserId !== null}
                >
                  {loadingFollowUpUserId === params.row.userId ? (
                    <CircularProgress size={14} thickness={5} />
                  ) : (
                    <EmailIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </MuiTooltip>
          </Box>
        ),
      },
      {
        field: 'queryCount',
        headerName: 'Queries',
        width: 80,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const count = params.value as number;
          if (count === 0) {
            return (
              <Typography variant="body2" color="text.secondary">
                0
              </Typography>
            );
          }
          return (
            <Button
              size="small"
              variant="text"
              onClick={(e) => {
                e.stopPropagation();
                openRequestsModal(params.row, 'query');
              }}
              sx={{
                minWidth: 'auto',
                p: 0.5,
                fontSize: '0.875rem',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'rgba(59, 130, 246, 0.1)',
                },
              }}
            >
              {count}
            </Button>
          );
        },
      },
      {
        field: 'workflowCount',
        headerName: 'Workflows',
        width: 90,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const count = params.value as number;
          if (count === 0) {
            return (
              <Typography variant="body2" color="text.secondary">
                0
              </Typography>
            );
          }
          return (
            <Button
              size="small"
              variant="text"
              onClick={(e) => {
                e.stopPropagation();
                openRequestsModal(params.row, 'workflow');
              }}
              sx={{
                minWidth: 'auto',
                p: 0.5,
                fontSize: '0.875rem',
                color: '#10b981',
                '&:hover': {
                  bgcolor: 'rgba(16, 185, 129, 0.1)',
                },
              }}
            >
              {count}
            </Button>
          );
        },
      },
      {
        field: 'projectCount',
        headerName: 'Projects',
        width: 80,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'totalEvents',
        headerName: 'Total Events',
        width: 100,
        align: 'center',
        headerAlign: 'center',
      },
    ],
    [
      openRequestsModal,
      knownTags,
      handleCsStatusChange,
      handleCsTagsChange,
      handleOpenUsageChat,
      handleSendFollowUp,
      loadingFollowUpUserId,
      showSnack,
    ]
  );

  const selectedOrgs = availableOrgs.filter((o) => selectedOrgIds.includes(o.id));

  const SummaryCard = ({
    title,
    value,
    percentage,
    color,
    isActive,
    onClick,
    tooltip,
    loading,
  }: {
    title: string;
    value: number;
    percentage?: number;
    color?: string;
    isActive?: boolean;
    onClick?: () => void;
    tooltip?: string;
    loading?: boolean;
  }) => (
    <Card
      onClick={onClick}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        border: isActive ? `2px solid ${color || '#8b5cf6'}` : '2px solid transparent',
        transition: 'border-color 0.2s, opacity 0.15s',
        opacity: loading ? 0.7 : 1,
        '&:hover': onClick
          ? {
              borderColor: color || '#8b5cf6',
            }
          : undefined,
      }}
    >
      <CardContent sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="overline" color="text.secondary">
            {title}
          </Typography>
          {tooltip && (
            <MuiTooltip
              title={tooltip}
              arrow
              placement="top"
              slotProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#1e293b',
                    fontSize: '0.75rem',
                    maxWidth: 280,
                    p: 1.5,
                  },
                },
              }}
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: 14,
                  color: 'text.secondary',
                  cursor: 'help',
                  '&:hover': { color: 'primary.main' },
                }}
              />
            </MuiTooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minHeight: 42 }}>
          {loading ? (
            <Skeleton variant="text" width={80} height={42} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
          ) : (
            <>
              <Typography variant="h4" fontWeight={600} sx={{ color: color || 'inherit' }}>
                {value.toLocaleString()}
              </Typography>
              {percentage !== undefined && percentage > 0 && (
                <Typography variant="body2" color="text.secondary">
                  ({percentage.toFixed(0)}%)
                </Typography>
              )}
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  if (isLoading && users.length === 0) {
    return (
      <Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} variant="rounded" height={100} sx={{ flex: 1 }} />
          ))}
        </Box>
        <Skeleton variant="rounded" height={500} />
      </Box>
    );
  }

  return (
    <Box>
      {isLoading && users.length > 0 && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            height: 3,
          }}
        />
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Users Status and Metrics
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadIcon />}
          onClick={handleExportCsv}
          disabled={filteredUsers.length === 0}
        >
          Export CSV
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <Autocomplete
            multiple
            size="small"
            options={availableOrgs}
            getOptionLabel={(option: FilterOption) => formatOrgDisplayName(option.name)}
            value={selectedOrgs}
            onChange={(_, newValue) => setSelectedOrgIds(newValue.map((v) => v.id))}
            renderInput={(params) => (
              <TextField {...params} label="Organizations" placeholder="All" />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.id}
                  label={formatOrgDisplayName(option.name)}
                  size="small"
                />
              ))
            }
            sx={{ width: 220, flex: '0 0 auto' }}
          />

          <TextField
            size="small"
            placeholder="Search name or email…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 180, flex: '1 1 200px', maxWidth: 280 }}
          />

          <Select
            size="small"
            displayEmpty
            value={csStatusFilter}
            onChange={(e) => setCsStatusFilter(e.target.value as CSOnboardingStatus | '')}
            renderValue={(value) =>
              value ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CSStatusDot status={value as CSOnboardingStatus} />
                  {CS_STATUS_LABELS[value as CSOnboardingStatus]}
                </Box>
              ) : (
                'CS Status: Any'
              )
            }
            sx={{ width: 170 }}
          >
            <MenuItem value="">CS Status: Any</MenuItem>
            {CS_STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                <CSStatusDot status={opt} />
                {CS_STATUS_LABELS[opt]}
              </MenuItem>
            ))}
          </Select>

          <Autocomplete
            multiple
            freeSolo
            size="small"
            options={knownTags}
            value={csTagFilter}
            onChange={(_, newValue) => setCsTagFilter(newValue as string[])}
            renderInput={(params) => <TextField {...params} label="CS Tags" placeholder="Any" />}
            sx={{ width: 180, flex: '0 0 auto' }}
          />

          <Select
            size="small"
            displayEmpty
            value={fvmFilter}
            onChange={(e) => setFvmFilter(e.target.value as '' | 'true' | 'false')}
            sx={{ width: 140 }}
          >
            <MenuItem value="">FVM: Any</MenuItem>
            <MenuItem value="true">FVM Done</MenuItem>
            <MenuItem value="false">FVM Pending</MenuItem>
          </Select>

          <Select
            size="small"
            displayEmpty
            value={atRiskFilter}
            onChange={(e) => setAtRiskFilter(e.target.value as '' | 'true' | 'false')}
            sx={{ width: 140 }}
          >
            <MenuItem value="">At Risk: Any</MenuItem>
            <MenuItem value="true">At Risk</MenuItem>
            <MenuItem value="false">Healthy</MenuItem>
          </Select>

          <Select
            size="small"
            displayEmpty
            value={stateTagFilter}
            onChange={(e) => setStateTagFilter(e.target.value as StateTag | '')}
            sx={{ width: 170 }}
          >
            <MenuItem value="">State: Any</MenuItem>
            {STATE_TAG_FILTER_OPTIONS.map((tag) => (
              <MenuItem key={tag} value={tag}>
                {STATE_TAG_LABELS[tag]}
              </MenuItem>
            ))}
          </Select>

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={hideLiteUsers}
                onChange={(e) => setHideLiteUsers(e.target.checked)}
              />
            }
            label={<Typography variant="body2" color="text.secondary">Hide Lite</Typography>}
            sx={{ ml: 'auto' }}
          />

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
            }
            label={<Typography variant="body2" color="text.secondary">Show Inactive</Typography>}
          />

          {statusFilter && (
            <Chip
              label={`Status: ${STATUS_LABELS[statusFilter]}`}
              onDelete={() => setStatusFilter(null)}
              size="small"
              sx={{
                bgcolor: STATUS_COLORS[statusFilter],
                color: 'white',
                '& .MuiChip-deleteIcon': { color: 'white' },
              }}
            />
          )}
        </Box>
      </Paper>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 180px', minWidth: 180 }}>
          <SummaryCard
            title="Total Users"
            value={derivedSummary.total}
            color="#8b5cf6"
            loading={isLoading}
            tooltip="Number of users in the table. Updates dynamically as you change any filter."
          />
        </Box>
        <Box sx={{ flex: '1 1 180px', minWidth: 180 }}>
          <SummaryCard
            title="Pre"
            value={derivedSummary.pre}
            percentage={derivedSummary.prePercentage}
            color={STATUS_COLORS.pre}
            isActive={statusFilter === 'pre'}
            onClick={() => handleStatusFilterClick('pre')}
            loading={isLoading}
            tooltip="Users with no activity logged yet. Click to filter the table by this status."
          />
        </Box>
        <Box sx={{ flex: '1 1 180px', minWidth: 180 }}>
          <SummaryCard
            title="In Progress"
            value={derivedSummary.inProgress}
            percentage={derivedSummary.inProgressPercentage}
            color={STATUS_COLORS.in_progress}
            isActive={statusFilter === 'in_progress'}
            onClick={() => handleStatusFilterClick('in_progress')}
            loading={isLoading}
            tooltip="Users with some activity but not yet fully onboarded. They haven't completed all onboarding criteria yet (1 query + 1 workflow + 1 project). Click to filter."
          />
        </Box>
        <Box sx={{ flex: '1 1 180px', minWidth: 180 }}>
          <SummaryCard
            title="Onboarded"
            value={derivedSummary.onboarded}
            percentage={derivedSummary.onboardedPercentage}
            color={STATUS_COLORS.onboarded}
            isActive={statusFilter === 'onboarded'}
            onClick={() => handleStatusFilterClick('onboarded')}
            loading={isLoading}
            tooltip="Users who completed onboarding. Criteria: at least 1 query, 1 workflow, and created 1 project. Click to filter."
          />
        </Box>
        <Box sx={{ flex: '1 1 180px', minWidth: 180 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="overline" color="text.secondary">
                  Hours Saved (30d)
                </Typography>
                <MuiTooltip
                  title={hoursSavedSummary
                    ? `${hoursSavedSummary.totalQueries} queries + ${hoursSavedSummary.totalWorkflows} workflows in the last 30 days. Calculation uses company-size-specific multipliers.`
                    : "Calculating hours saved based on query and workflow usage..."
                  }
                  arrow
                  placement="top"
                  slotProps={{
                    tooltip: {
                      sx: {
                        bgcolor: '#1e293b',
                        fontSize: '0.75rem',
                        maxWidth: 280,
                        p: 1.5,
                      },
                    },
                  }}
                >
                  <InfoOutlinedIcon
                    sx={{
                      fontSize: 14,
                      color: 'text.secondary',
                      cursor: 'help',
                      '&:hover': { color: 'primary.main' },
                    }}
                  />
                </MuiTooltip>
              </Box>
              {isHoursSavedLoading ? (
                <Box>
                  <Skeleton variant="text" width={80} height={40} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      component="span"
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: '#10b981',
                        animation: 'pulse 1.5s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 100%': { opacity: 0.4 },
                          '50%': { opacity: 1 },
                        },
                      }}
                    />
                    Calculating...
                  </Typography>
                </Box>
              ) : hoursSavedSummary ? (
                <Typography variant="h4" fontWeight={600} sx={{ color: '#10b981' }}>
                  {hoursSavedSummary.totalHoursSaved.toLocaleString()}h
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No data
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Data Grid */}
      <Paper sx={{ height: 600 }}>
        <DataGrid
          rows={filteredUsers}
          columns={columns}
          getRowId={(row) => row.userId}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 50 } },
          }}
          disableRowSelectionOnClick
          density="compact"
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell': {
              borderColor: 'rgba(255,255,255,0.05)',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: 'rgba(0,0,0,0.2)',
            },
            '& .MuiDataGrid-row:hover': {
              bgcolor: 'rgba(255,255,255,0.02)',
            },
          }}
        />
      </Paper>

      {/* User Requests Modal */}
      <UserRequestsModal
        open={requestsModal.open}
        onClose={closeRequestsModal}
        userId={requestsModal.userId}
        userName={requestsModal.userName}
        requestType={requestsModal.requestType}
        requestCount={requestsModal.requestCount}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        message={snack.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
